import { useContext } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useParams } from 'react-router-dom'
import { useActiveInstallation } from 'src/Hooks'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { calculateFromBasedOnView, calculatePreviousFromBasedOnView, calculatePreviousToBasedOnView } from '../utils'
import { SummaryTimeView } from '../types'
import moment from '@e3dc-react/shell/Libs/moment'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { useStorageListForMissingFarmData } from './use-storage-id-to-get-list'
import { wallboxErrorCodes } from 'src/Libs/dynamic-power-prices/api/error-codes'
import { useBatterySavings, useWallboxSavings } from 'src/Libs/dynamic-power-prices/hooks/use-three-sixty-savings'
import { ThreeSixtySavingsResolution } from 'src/Libs/dynamic-power-prices/types'
import { DataCardProps } from 'src/Elements/DataCard/DataCard'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'
import { SystemSettingHashes } from 'src/Libs/navigation/types'
import { isFlowTheme } from 'src/Libs/utils'
import { useFarmContext } from 'src/Elements/Farm'
import { useIsTariffRedirectDisabled } from 'src/Libs/system-settings/hooks/use-is-tariff-redirect-disabled'

interface SubtitleData {
  text: string
  value?: string
}

export type UseThreeSixtySavingDataCardProps = Omit<DataCardProps, 'icon' | 'subTitle'> & {
  isDppEnabled: boolean
  subTitle: SubtitleData
  isLoading: boolean
}

/*
 * This hook is responsible for returning the 360 data card props
 */
const useThreeSixtySavingDataCardProps = (period: SummaryTimeView): UseThreeSixtySavingDataCardProps => {
  const { t } = useCustomTranslation()
  const props: UseThreeSixtySavingDataCardProps = {
    headline: t('dashboard.dataCards.360.card.title-text'),
    value: '',
    subTitle: {
      text: '',
    },
    link: undefined,
    isDppEnabled: false,
    isLoading: false,
  }
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()

  const isTariffRedirectDisabled = useIsTariffRedirectDisabled()
  const { isDynamicPowerPriceEnabled, isDynamicPowerPriceEnabledForWallboxes, isDynamicPowerPriceEnabledForBattery } =
    useContext(DynamicPowerPricesEnabledContext)
  const { isLoading: installationIsLoading } = useActiveInstallation(true, installationId, storageId)
  const { data: wallboxes } = useWallboxHardwareByStorage()

  const storagesList: string[] = useStorageListForMissingFarmData(storageId)
  const previousFrom = calculatePreviousFromBasedOnView(period)
  const from = calculateFromBasedOnView(period)
  const to = moment().add(1, 'day').endOf('day')
  const previousTo = calculatePreviousToBasedOnView(period, previousFrom)
  const resolution = getResolutionToFetchDataBy(period)
  const { isFarm } = useFarmContext()

  const {
    data: wallboxData,
    errorCode,
    hasError,
    isLoading: isWallboxSavingsLoading,
  } = useWallboxSavings({ storagesIds: storagesList, isFarm, from, to, isEnabled: isDynamicPowerPriceEnabledForWallboxes, resolution })

  const wallboxSavingsPreviousValues = useWallboxSavings({
    storagesIds: storagesList,
    isFarm,
    from: previousFrom,
    to: previousTo,
    isEnabled: isDynamicPowerPriceEnabledForWallboxes,
    resolution,
  })

  const { data: batteryData, isLoading: isBatterySavingsDataLoading } = useBatterySavings({
    storagesIds: storagesList,
    isFarm,
    from,
    to,
    isEnabled: isDynamicPowerPriceEnabledForBattery,
    resolution,
  })

  const batterySavingsPreviousValues = useBatterySavings({
    storagesIds: storagesList,
    isFarm,
    from: previousFrom,
    to: previousTo,
    isEnabled: isDynamicPowerPriceEnabledForBattery,
    resolution,
  })

  const isLoading = installationIsLoading || isWallboxSavingsLoading || isBatterySavingsDataLoading

  const getFormattedValue = (value: number): string => {
    return t('dashboard.dataCards.360.saving', {
      value: getLocalizedNumber(value ?? 0),
    })
  }
  const currentWallboxSavings = wallboxData.wallboxSavings
  const previousWallboxSavings = wallboxSavingsPreviousValues.data.wallboxSavings
  const currentBatterySavings = batteryData.batterySavings
  const previousBatterySavings = batterySavingsPreviousValues.data.batterySavings
  const currentThreeSixtySavingsTotalSum = currentBatterySavings + currentWallboxSavings
  const previousThreeSixtySavingsTotalSum = previousBatterySavings + previousWallboxSavings
  const flowLink = `/installation/${installationId}/${storageId}/properties/advanced-settings/general${SystemSettingHashes.AdvancedSettingsTariffsPriceLimit}`

  const areWallboxesPricesSet =
    errorCode !== wallboxErrorCodes.wallboxPriceNotSet &&
    wallboxSavingsPreviousValues?.errorCode !== wallboxErrorCodes.wallboxPriceNotSet &&
    hasError === false &&
    wallboxSavingsPreviousValues?.hasError === false

  const hasWallboxes = wallboxes?.length > 0 && errorCode !== wallboxErrorCodes.hasNoWallboxes

  if (!isLoading) {
    props.isDppEnabled = isDynamicPowerPriceEnabled

    if ((isDynamicPowerPriceEnabledForWallboxes && hasWallboxes && areWallboxesPricesSet) || isDynamicPowerPriceEnabledForBattery) {
      props.subTitle = {
        text: t('dashboard.dataCards.sub-title-text'),
        value: getFormattedValue(previousThreeSixtySavingsTotalSum),
      }
      props.value = getFormattedValue(currentThreeSixtySavingsTotalSum)
    }

    if (isDynamicPowerPriceEnabledForWallboxes && hasWallboxes && !areWallboxesPricesSet) {
      props.subTitle = {
        text: t('dashboard.dataCards.360.card.reference-price-missing'),
      }
      props.link = isFlowTheme ? flowLink : `/e-mobility/${installationId}/wallbox`
    }

    if (isDynamicPowerPriceEnabledForWallboxes && !hasWallboxes) {
      props.subTitle = {
        text: t('dashboard.dataCards.360.has-no-wallbox'),
      }
    }

    if (!isDynamicPowerPriceEnabled) {
      if (isTariffRedirectDisabled) {
        props.subTitle = { text: t('dashboard.dataCards.360.inactive-offline') }
      } else {
        props.subTitle = {
          text: t('dashboard.dataCards.360.inactive'),
        }
        props.link = `/installation/${installationId}/${storageId}/properties/tariffs`
      }
    }
  } else {
    if (!isDynamicPowerPriceEnabled) props.headline = ''
    props.isLoading = true
  }

  return props
}

export default useThreeSixtySavingDataCardProps

/**
 * Get the resolution (data aggregated by day or month) to fetch data by
 *
 * @param period
 * @returns
 */
const getResolutionToFetchDataBy = (period: SummaryTimeView): ThreeSixtySavingsResolution => {
  if (period === 'month' || period === 'year') return 'month'
  return 'day'
}
